import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import { Calendar, Select, Icon, Spin, Modal, message } from "antd";

const { Option } = Select;
import {
    fetchTeamsCalls,
    fetchTeamsCallDetail,
    cancelTeamsCall,
    teamsCallsPage,
    normalizeCallStatus,
    callStatusLabel
} from "services/telephony-service";
import { BLOCKING_STATUSES } from "constants/teams-calls";

// status -> emoji, used in the agenda + calendar chips for a bit of personality.
const STATUS_EMOJI = {
    scheduled: "🗓️",
    in_progress: "🔴",
    completed: "✅",
    artefacts_pending: "⏳",
    cancelled: "🚫",
    failed: "⚠️"
};

const greeting = () => {
    const h = moment().hour();
    if (h < 12) return { text: "Good morning", emoji: "☀️" };
    if (h < 17) return { text: "Good afternoon", emoji: "🌤️" };
    if (h < 21) return { text: "Good evening", emoji: "🌆" };
    return { text: "Burning the midnight oil", emoji: "🌙" };
};

// App-blue theme. Full-height flex layout so the drawer fills the screen and
// nothing scrolls (the month grid + a day's agenda fit side by side).
const SCOPED_CSS = `
.mycal {
  --g1:#2c5fe0; --g2:#1e46b8; --g3:#38bdf8;
  --ink:#12213f; --slate:#5b6472; --mist:#93a0b5; --line:#e6ecf6;
  flex:1 1 auto; min-height:0; display:flex; flex-direction:column;
}
.mycal * { box-sizing:border-box; }

.mycal .mc-hero {
  flex:0 0 auto; position:relative; overflow:hidden; border-radius:22px; padding:18px 22px; color:#fff;
  background:linear-gradient(120deg,#2453d6 0%,#2c5fe0 46%,#38bdf8 100%);
  box-shadow:0 20px 42px -24px rgba(44,95,224,.75);
}
.mycal .mc-hero::after {
  content:""; position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(150px 150px at 88% 12%, rgba(255,255,255,.38), transparent 70%);
}
.mycal .mc-hero__row { position:relative; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; }
.mycal .mc-hero__hi { font-size:13px; font-weight:800; opacity:.92; }
.mycal .mc-hero__title { font-size:24px; font-weight:800; letter-spacing:-.5px; margin-top:3px; line-height:1.1; }
.mycal .mc-pills { position:relative; display:flex; gap:10px; margin-top:14px; flex-wrap:wrap; }
.mycal .mc-pill {
  background:rgba(255,255,255,.2); border:1px solid rgba(255,255,255,.32); backdrop-filter:blur(8px);
  border-radius:15px; padding:7px 13px; font-size:13px; font-weight:800; display:flex; align-items:center; gap:8px;
}
.mycal .mc-pill b { font-size:18px; font-variant-numeric:tabular-nums; }
.mycal .mc-refresh {
  background:rgba(255,255,255,.24); border:1px solid rgba(255,255,255,.36); color:#fff; border-radius:13px;
  height:38px; padding:0 15px; font-weight:800; font-size:13px; cursor:pointer; display:inline-flex; align-items:center; gap:7px; transition:.15s;
}
.mycal .mc-refresh:hover { background:rgba(255,255,255,.36); transform:translateY(-1px); }

.mycal .mc-grid { flex:1 1 auto; min-height:0; display:grid; grid-template-columns:1.7fr .85fr; gap:14px; margin-top:14px; }
@media (max-width:900px){ .mycal .mc-grid { grid-template-columns:1fr; overflow-y:auto; } }
.mycal .mc-card { background:#fff; border:1px solid var(--line); border-radius:20px; padding:12px 14px; box-shadow:0 12px 30px -24px rgba(18,33,63,.5); min-height:0; }
.mycal .mc-card--cal { display:flex; flex-direction:column; overflow:hidden; }
.mycal .mc-card--cal .mc-calwrap { flex:1 1 auto; min-height:0; overflow:auto; }
.mycal .mc-card--agenda { overflow-y:auto; }

/* compact the month grid so it fits the available height without scrolling */
.mycal .ant-fullcalendar-fullscreen .ant-fullcalendar-content { height:auto; min-height:42px; }
.mycal .ant-fullcalendar-fullscreen .ant-fullcalendar-date { min-height:60px; padding:2px 6px 0; }

.mycal .mc-calnav { display:flex; align-items:center; gap:8px; padding:2px 2px 12px; flex-wrap:wrap; }
.mycal .mc-navbtn { width:30px; height:30px; border-radius:9px; border:1px solid var(--line); background:#fff; color:var(--slate); cursor:pointer; display:flex; align-items:center; justify-content:center; transition:.13s; }
.mycal .mc-navbtn:hover { border-color:var(--g1); color:var(--g1); }
.mycal .mc-today { margin-left:auto; height:30px; padding:0 15px; border-radius:9px; border:none; color:#fff; font-weight:800; font-size:12px; cursor:pointer; background:linear-gradient(120deg,var(--g1),var(--g3)); }
.mycal .mc-today:hover { filter:brightness(1.05); }

.mycal .mc-ev {
  font-size:11px; font-weight:800; line-height:1.5; border-radius:8px; padding:1px 8px; margin-bottom:3px;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#fff;
  background:linear-gradient(120deg,var(--g1),var(--g3));
}
.mycal .mc-ev--cancelled { background:#eef1f7; color:#9aa4b6; text-decoration:line-through; }
.mycal .mc-ev--completed { background:linear-gradient(120deg,#22c55e,#84cc16); }
.mycal .mc-more { font-size:10px; font-weight:800; color:var(--g1); padding:0 6px; }

.mycal .mc-agenda__title { font-size:14px; font-weight:800; color:var(--ink); margin-bottom:12px; display:flex; align-items:center; gap:8px; }
.mycal .mc-item { display:flex; gap:12px; padding:12px 13px; border-radius:16px; margin-bottom:9px; background:linear-gradient(180deg,#f4f8ff,#ffffff); border:1px solid var(--line); transition:.16s; }
.mycal .mc-item:hover { transform:translateY(-2px); box-shadow:0 16px 32px -20px rgba(44,95,224,.5); border-color:#cfe0fb; }
.mycal .mc-item__time { font-variant-numeric:tabular-nums; font-weight:800; font-size:13px; color:var(--g1); min-width:62px; }
.mycal .mc-item__subj { font-weight:800; font-size:14px; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.mycal .mc-item__meta { font-size:12px; color:var(--mist); margin-top:2px; font-weight:600; }
.mycal .mc-badge { font-size:11px; font-weight:800; padding:3px 11px; border-radius:999px; }
.mycal .mc-badge--scheduled { background:linear-gradient(120deg,#2c5fe0,#38bdf8); color:#fff; }
.mycal .mc-badge--in_progress { background:linear-gradient(120deg,#22c55e,#84cc16); color:#fff; }
.mycal .mc-badge--completed { background:#eaeef6; color:#5b6472; }
.mycal .mc-badge--artefacts_pending { background:linear-gradient(120deg,#f59e0b,#fbbf24); color:#fff; }
.mycal .mc-badge--cancelled, .mycal .mc-badge--failed { background:#ffe4e6; color:#e11d48; }
.mycal .mc-case { font-size:11px; font-weight:800; padding:3px 10px; border-radius:999px; background:#eef3ff; color:var(--g1); border:1px solid #d7e4fd; display:inline-flex; align-items:center; gap:5px; }
.mycal .mc-link { border:none; background:none; font-weight:800; font-size:13px; cursor:pointer; padding:0 6px; }
.mycal .mc-link--join { color:var(--g1); }
.mycal .mc-link--cancel { color:#e11d48; }
.mycal .mc-empty { text-align:center; padding:22px 6px; }
.mycal .mc-empty__emoji { font-size:38px; }
.mycal .mc-empty__text { font-weight:800; color:var(--ink); margin-top:6px; }
.mycal .mc-empty__sub { font-size:12px; color:var(--mist); font-weight:600; margin-top:2px; }

.mycal .ant-fullcalendar-selected-day .ant-fullcalendar-date { background:linear-gradient(180deg,#eef4ff,#f0f8ff) !important; border-color:var(--g1) !important; }
.mycal .ant-fullcalendar-today .ant-fullcalendar-date { border-color:var(--g3); }
.mycal .ant-fullcalendar-date:hover { background:#f2f7ff; }
.mycal .ant-fullcalendar-column-header-inner { color:var(--mist); font-weight:800; }

@media (prefers-reduced-motion: reduce){
  .mycal .mc-item, .mycal .mc-refresh { transition:none; }
  .mycal .mc-item:hover, .mycal .mc-refresh:hover { transform:none; }
}
`;

/**
 * My Calls — a personal month calendar of the Teams calls the logged-in user has
 * scheduled (telephony list API filtered by user). Not case-scoped; built for a
 * full-screen header-menu drawer. Playful, app-blue styling.
 */
class MyCallsCalendar extends React.Component {
    state = {
        calls: [],
        loading: true,
        selected: moment().startOf("day")
    };

    componentDidMount() {
        this.loadCalls();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.userId !== this.props.userId) {
            this.loadCalls();
        }
    }

    loadCalls = () => {
        const { userId } = this.props;
        if (userId == null) {
            this.setState({ calls: [], loading: false });
            return;
        }
        this.setState({ loading: true });
        this.fetchAll(userId)
            .then(rows => this.setState({ calls: rows, loading: false }))
            .catch(() => this.setState({ calls: [], loading: false }));
    };

    // Sequentially pull up to `maxPages` pages so the month view isn't truncated.
    fetchAll = (userId, page = 1, acc = [], maxPages = 6) =>
        fetchTeamsCalls({ userId, page }).then(({ data }) => {
            const { results, next } = teamsCallsPage(data);
            // status now arrives as an integer code — normalise to the string key.
            const rows = results.map(c => ({
                ...c,
                status: normalizeCallStatus(c.status)
            }));
            const all = acc.concat(rows);
            const hasNext = Boolean(next) && rows.length > 0;
            if (hasNext && page < maxPages) {
                return this.fetchAll(userId, page + 1, all, maxPages);
            }
            return all;
        });

    callsOn = date =>
        (this.state.calls || [])
            .filter(c => {
                const s = moment(c.scheduled_start);
                return s.isValid() && s.isSame(date, "day");
            })
            .sort(
                (a, b) =>
                    moment(a.scheduled_start).valueOf() -
                    moment(b.scheduled_start).valueOf()
            );

    // Any change to the calendar value — a date click OR month/year navigation —
    // moves the selected day. Required because the Calendar is controlled.
    onPick = d => this.setState({ selected: d.clone().startOf("day") });

    // Custom calendar header. antd's default fullscreen header doesn't reliably
    // drive a CONTROLLED value, so we render our own month + year filters that
    // update state directly (guaranteed to work).
    renderCalHeader = ({ value }) => {
        const months = moment.monthsShort();
        const year = value.year();
        const years = [];
        for (let y = year - 3; y <= year + 3; y += 1) {
            years.push(y);
        }
        return (
            <div className={"mc-calnav"}>
                <button
                    type={"button"}
                    className={"mc-navbtn"}
                    aria-label={"Previous month"}
                    onClick={() => this.onPick(value.clone().subtract(1, "month"))}
                >
                    <Icon type={"left"} />
                </button>
                <Select
                    size={"small"}
                    value={value.month()}
                    dropdownMatchSelectWidth={false}
                    onChange={m => this.onPick(value.clone().month(m))}
                    style={{ width: 78 }}
                >
                    {months.map((mo, i) => (
                        <Option key={i} value={i}>
                            {mo}
                        </Option>
                    ))}
                </Select>
                <Select
                    size={"small"}
                    value={year}
                    dropdownMatchSelectWidth={false}
                    onChange={y => this.onPick(value.clone().year(y))}
                    style={{ width: 82 }}
                >
                    {years.map(y => (
                        <Option key={y} value={y}>
                            {y}
                        </Option>
                    ))}
                </Select>
                <button
                    type={"button"}
                    className={"mc-navbtn"}
                    aria-label={"Next month"}
                    onClick={() => this.onPick(value.clone().add(1, "month"))}
                >
                    <Icon type={"right"} />
                </button>
                <button
                    type={"button"}
                    className={"mc-today"}
                    onClick={() => this.onPick(moment())}
                >
                    Today
                </button>
            </div>
        );
    };

    dateCellRender = value => {
        const items = this.callsOn(value);
        if (!items.length) {
            return null;
        }
        const shown = items.slice(0, 3);
        return (
            <div>
                {shown.map(c => {
                    const t = moment(c.scheduled_start).format("h:mm A");
                    const mod =
                        c.status === "cancelled"
                            ? " mc-ev--cancelled"
                            : c.status === "completed"
                            ? " mc-ev--completed"
                            : "";
                    return (
                        <div
                            key={c.id}
                            className={"mc-ev" + mod}
                            title={`${t} · ${c.subject || "Call"}${
                                c.case_id ? ` · Case ${c.case_id}` : ""
                            } (${c.status})`}
                        >
                            {t} · {c.subject || "Call"}
                        </div>
                    );
                })}
                {items.length > 3 ? (
                    <div className={"mc-more"}>+{items.length - 3} more ✨</div>
                ) : null}
            </div>
        );
    };

    handleJoin = call => {
        // The list already carries join_url — prefer it; fall back to detail.
        if (call.join_url) {
            window.open(call.join_url, "_blank", "noopener,noreferrer");
            return;
        }
        if (!call.meeting_id) {
            message.info("Meeting link is not ready yet.");
            return;
        }
        const hide = message.loading("Opening meeting…", 0);
        fetchTeamsCallDetail(call.meeting_id)
            .then(({ data }) => {
                hide();
                const d = (data && data.data) || data || {};
                if (d.join_url) {
                    window.open(d.join_url, "_blank", "noopener,noreferrer");
                } else {
                    message.error("Meeting link unavailable.");
                }
            })
            .catch(() => {
                hide();
                message.error("Could not open the meeting link.");
            });
    };

    handleCancel = call => {
        if (!call.meeting_id) {
            message.error("This call can't be cancelled (no meeting id).");
            return;
        }
        Modal.confirm({
            title: "Cancel this call?",
            content: `"${call.subject}" — attendees will be notified.`,
            okText: "Cancel call",
            okType: "danger",
            cancelText: "Keep",
            onOk: () =>
                cancelTeamsCall(call.meeting_id)
                    .then(() => {
                        message.success("Call cancelled.");
                        this.loadCalls();
                    })
                    .catch(() => message.error("Could not cancel the call."))
        });
    };

    renderAgenda() {
        const { selected, loading } = this.state;
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: 26 }}>
                    <Spin />
                </div>
            );
        }
        const items = this.callsOn(selected);
        const isToday = selected.isSame(moment(), "day");
        if (!items.length) {
            return (
                <div className={"mc-empty"}>
                    <div className={"mc-empty__emoji"}>{isToday ? "🌱" : "🎉"}</div>
                    <div className={"mc-empty__text"}>
                        {isToday ? "No calls today — go touch grass" : "All clear here"}
                    </div>
                    <div className={"mc-empty__sub"}>
                        Nothing on {selected.format("ddd, DD MMM")} ✨
                    </div>
                </div>
            );
        }
        return items.map(call => {
            const start = moment(call.scheduled_start);
            const end = moment(call.scheduled_end);
            const canJoin = BLOCKING_STATUSES.includes(call.status);
            const canCancel = call.status === "scheduled";
            const status = call.status || "scheduled";
            return (
                <div key={call.id} className={"mc-item"}>
                    <div className={"mc-item__time"}>
                        {start.isValid() ? start.format("h:mm A") : "—"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={"mc-item__subj"}>
                            {STATUS_EMOJI[status] || "🗓️"}{" "}
                            {call.subject || "Untitled call"}
                        </div>
                        <div className={"mc-item__meta"}>
                            {start.isValid() && end.isValid()
                                ? `${start.format("h:mm A")}–${end.format("h:mm A")}`
                                : ""}
                        </div>
                        <div
                            style={{
                                marginTop: 8,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexWrap: "wrap"
                            }}
                        >
                            <span className={"mc-badge mc-badge--" + status}>
                                {callStatusLabel(status)}
                            </span>
                            {call.case_id ? (
                                <span className={"mc-case"}>
                                    <Icon type={"folder"} /> Case {call.case_id}
                                </span>
                            ) : null}
                            {canJoin ? (
                                <button
                                    className={"mc-link mc-link--join"}
                                    onClick={() => this.handleJoin(call)}
                                >
                                    Join →
                                </button>
                            ) : null}
                            {canCancel ? (
                                <button
                                    className={"mc-link mc-link--cancel"}
                                    onClick={() => this.handleCancel(call)}
                                >
                                    Cancel
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            );
        });
    }

    render() {
        const { selected, calls, loading } = this.state;
        const { userName } = this.props;
        const g = greeting();
        const firstName = (userName || "").split(" ")[0] || "there";
        const upcoming = (calls || []).filter(
            c =>
                BLOCKING_STATUSES.includes(c.status) &&
                moment(c.scheduled_start).isSameOrAfter(moment(), "day")
        ).length;
        const thisMonth = (calls || []).filter(c =>
            moment(c.scheduled_start).isSame(moment(), "month")
        ).length;

        return (
            <div className={"mycal"}>
                <style>{SCOPED_CSS}</style>

                <div className={"mc-hero"}>
                    <div className={"mc-hero__row"}>
                        <div>
                            <div className={"mc-hero__hi"}>
                                {g.text}, {firstName} {g.emoji}
                            </div>
                            <div className={"mc-hero__title"}>
                                Your call calendar 📞
                            </div>
                            <div className={"mc-pills"}>
                                <span className={"mc-pill"}>
                                    🔥 <b>{upcoming}</b> upcoming
                                </span>
                                <span className={"mc-pill"}>
                                    🗓️ <b>{thisMonth}</b> this month
                                </span>
                            </div>
                        </div>
                        <button className={"mc-refresh"} onClick={this.loadCalls}>
                            {loading ? <Icon type={"loading"} /> : <Icon type={"reload"} />}
                            Refresh
                        </button>
                    </div>
                </div>

                <div className={"mc-grid"}>
                    <div className={"mc-card mc-card--cal"}>
                        <div className={"mc-calwrap"}>
                            <Calendar
                                dateCellRender={this.dateCellRender}
                                value={selected}
                                headerRender={this.renderCalHeader}
                                onSelect={this.onPick}
                            />
                        </div>
                    </div>
                    <div className={"mc-card mc-card--agenda"}>
                        <div className={"mc-agenda__title"}>
                            📌 {selected.format("ddd, DD MMM")}
                        </div>
                        {this.renderAgenda()}
                    </div>
                </div>
            </div>
        );
    }
}

MyCallsCalendar.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userName: PropTypes.string
};

const mapStateToProps = state => {
    const data = state.user.getIn(["data"]);
    const get = k => (data && data.get ? data.get(k) : undefined);
    const fullName =
        get("full_name") ||
        [get("first_name"), get("last_name")].filter(Boolean).join(" ").trim() ||
        get("email") ||
        null;
    return { userId: get("id"), userName: fullName };
};

export default connect(mapStateToProps)(MyCallsCalendar);
